import { FC, useMemo } from 'react'
import { FutureLayerUID, FutureLayer } from 'components/features/future/types'
import { FuturePanePreset } from 'components/features/future/FuturePanePreset/FuturePanePreset'
import * as yup from 'yup'
import { useLayerSelector, extendCommonSchema } from 'components/features/future/helpers'
import { FutureWhenInput } from 'components/features/future/FutureWhenInput/FutureWhenInput'
import { schemaNumberInString } from 'schemas/baseSchema'
import { FutureFieldButtons } from 'components/features/future/FutureField/FutureFieldButtons'
import { maskPercentFromZero } from 'components/atoms/inputs/maskedInput'
import { useCollectLayerErrors } from 'components/features/future/hooks/useCollectErrors'
import { ObjectSchema } from 'yup'
import { LayerStatus } from 'components/features/future/LayerStatus/LayerStatus'

const LAYER_SLUG = 'retirement' as const
type LayerSlugType = typeof LAYER_SLUG
type LayerType = Extract<FutureLayer, { slug: LayerSlugType }>

const HARDCODED_REDUCTION_RATE = 25

const schemaCreator = extendCommonSchema<LayerSlugType>(yup.object({
  spending_reduction_rate: schemaNumberInString,
}) as ObjectSchema<LayerType>)

type FutureLayerPaneRetireEarlyProps = {
  uid?: FutureLayerUID
  onCancel: () => void
}

export const FutureLayerPaneRetireEarly: FC<FutureLayerPaneRetireEarlyProps> = (props) => {
  const { onCancel, uid } = props
  const layer = useLayerSelector<LayerSlugType>(props)

  const layerWidthDefaults = useMemo(() => ({
    when: {
      slug: 'at_age',
      age: 67,
    },
    spending_reduction_rate: HARDCODED_REDUCTION_RATE,
    ...layer,
  } as typeof layer), [layer])

  return (
    <FuturePanePreset<LayerSlugType>
      layer={ layerWidthDefaults }
      slug={ LAYER_SLUG }
      name='Retire'
      icon='palms'
      type='goal'
      schemaCreator={ schemaCreator }
      onCancel={ onCancel }
    >
      <FormFields uid={ uid } />
    </FuturePanePreset>
  )
}

type FormFieldsProps = {
  uid?: FutureLayerUID
}
const FormFields: FC<FormFieldsProps> = (props) => {
  const { uid } = props

  const errors = useCollectLayerErrors<LayerType>()

  const completeChild = useMemo(() => (
    <h1>
      Goal Completed
    </h1>
  ), [])

  return (
    <LayerStatus uid={ uid || null } completeContent={ completeChild } skipPartialState type='goal'>
      <FutureWhenInput error={ errors.whenField } />

      <FutureFieldButtons
        label='Spending reduction after retirement'
        name='spending_reduction_rate'
        mask={ maskPercentFromZero }
        inputMode='decimal'

        error={ errors.spending_reduction_rate }
        note={ `The average person reduces their spending by ${ HARDCODED_REDUCTION_RATE }% after retirement` }

        min={ 0 }
        max={ 100 }
        step={ 1 }
      />

    </LayerStatus>
  )
}
